/**
 * The MONSTER stream's numbers (spec 02 §4.1, §5, §6), each traced to the rebb64 routine it comes
 * from. Positions are VIC sprite registers (see types.ts); "I-tick" is one call of the IRQ's sprite
 * handler D_1CBD, "M-tick" one main-loop pass.
 */

import type { MonsterKind } from '../../levels/types';

/** Drop-in: every monster starts at this Y ($17BE `lda #$15; sta $C4,x`) and slides down to its spawn row. */
export const DROP_START_Y = 0x15;
/** Drop-in step, px per I-tick (L_1CA0, and the frozen loop at $17D1). */
export const DROP_STEP = 2;
/** Attack cooldown set when the drop-in ends in play (L_1CAD `lda #$0a; sta D_8890,x`, 02 §5). */
export const DROP_COOLDOWN = 10;

/** Walking, px per AI update (D_ECDF / D_ED1E: `lda #$fe` / `lda #$02`). */
export const WALK_STEP = 2;
/** Falling, px per AI update (D_EB3F `adc #$02`), also the escape fall (D_273D). */
export const FALL_STEP = 2;
/** Falling through Y $F5 re-enters at $15 (D_EB3F, D_273D). */
export const WRAP_FROM_Y = 0xf5;
export const WRAP_TO_Y = 0x15;
/** Above this Y no tile is checked: a monster always falls (L_EA6B, D_EB3F, D_EC3C `cmp #$1f`). */
export const TOP_Y = 0x1f;
/** Standing Y values are $2D + 8n (D_EB3F `sbc #$2d; and #$07`, L_EC70 snap). */
export const LINE_Y = 0x2d;
/** Rightmost X a drifting jump may reach (L_ECB0 `cmp #$f4`); also the escape clamp (D_2808). */
export const MAX_X = 0xf4;
/** Leftmost X after an escape (D_2808 `lda #$24`). */
export const MIN_X = 0x24;

/**
 * Vertical-jump rise and descent, px per tick, indexed by the step counter ($ACDD): the rise reads
 * it from index 15 down to 1 (41 px), the descent from 0 up to 15 (0 at the apex, then 41 px).
 */
export const JUMP_TABLE: readonly number[] = [0, 1, 1, 1, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 4];
/** Gap-jump descent, px per tick ($ACED): 0 at the apex, then 1 px for 15 ticks. */
export const GAP_TABLE: readonly number[] = [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1];
/** A vertical jump starts its counter here: $1F..$10 crouch (16 ticks), $0F..$01 rise (D_EBC4). */
export const CROUCH_START = 0x1f;
/** A gap jump starts its counter here: no crouch, 15 ticks rising 1 px (L_EAF2). */
export const GAP_START = 0x0f;
/** Descent steps before a jump ends without landing (L_EC17 `cpy #$10`). */
export const DESCENT_STEPS = 16;
/** The gap jump looks this many map bytes past the column ahead ($04 = cell pointer +9 / -9, L_EA92). */
export const GAP_REACH = 9;

/**
 * Anger (02 §4.1): an angry monster gets an extra AI update on I-ticks where the frame counter has
 * bit 1 clear (L_1D03 `lda $08; and #$02`); the I-tick itself runs on odd frames, so frames 4n+1.
 */
export const ANGRY_FRAME_MASK = 3;
export const ANGRY_FRAME_PHASE = 1;

/** Contact: |Ex - (Px+2)| < 12 and |Ey - (Py+2)| < 12 kills (L_1D32..L_1D6D). */
export const CONTACT_OFFSET = 2;
export const CONTACT_RANGE = 12;

/** The C64's state number for each kind: type + 2 (entity-spawn.s $39FB `adc #$02`). */
export const KIND_STATE: Readonly<Record<MonsterKind, number>> = {
  zenChan: 2, hidegons: 3, banebou: 4, pulpul: 5, monsta: 6, drunk: 7, mighta: 8, invader: 9,
};
/**
 * Frames per facing group, by state ($AB79, read past its end into $AB81 for states 8 and 9); frame >=
 * this faces left, and the walk cycle wraps within a group (mask = this - 1).
 */
export const FACE_FRAMES: readonly number[] = [0x0a, 0x14, 4, 4, 4, 4, 4, 2, 2, 0];
/**
 * Base of the jump-permission toggle interval, by state ($AB71, read past its end into $AB79 for
 * states 8 and 9): interval = base + rand(0..31), rolled once at spawn ($3A36).
 */
export const INTERVAL_BASE: readonly number[] = [0xb3, 0xbb, 0x1e, 0x19, 0x00, 0x0f, 0x14, 0x14, 0x0a, 0x14];
export const INTERVAL_RAND = 32;

/**
 * C64 colour numbers of each kind's sprite: $AB61 by state, read past its end into $AB69 for
 * Mighta and Invader ($3A3F; the spec's "$AB63 by type"). Angry: light red, 10 (R§3).
 */
export const SPRITE_COLOUR: Readonly<Record<MonsterKind, number>> = {
  zenChan: 12, hidegons: 15, banebou: 5, pulpul: 13, monsta: 4, drunk: 5, mighta: 3, invader: 15,
};
export const ANGRY_COLOUR = 10;

/** Baron von Blubba (02 §6; the real handler is at $1473, mislabelled `player_death_handler`). */
export const BARON = {
  /** Start corner, software-sprite column 2 or 27 and row 5 or 25 ($A777, $A779; one roll each). */
  cols: [0x02, 0x1b] as const,
  rows: [0x05, 0x19] as const,
  /** Pause before every leg, M-ticks ($14C6 `lda #$1e; sta D_A775,x`). */
  pause: 0x1e,
  /** Legs grow 1, 2, 3 ... and stop growing at 15 ($14B6 `and #$0f`). */
  maxLeg: 15,
  /** Kill box: |Px - (8col + $18)| < 16 and |Py - (8row + $15)| < 16 (L_152B). */
  hitX: 0x18,
  baseY: 0x15,
  reach: 16,
  /** The sim keeps the Baron at x = 8col + $14 (like a sprite on char column col), y = 8row + $15. */
  baseX: 0x14,
} as const;

/** Colour of a monster frozen by the power heart: light blue (D_2785 `lda #$0e; sta D_8548,x`). */
export const FROZEN_COLOUR = 14;

/**
 * Attacks of Hidegons, Drunk and Mighta (02 §5 "Attack rule"): D_EDCD decides, D_ED3B winds up,
 * L_ED41 lets the shot go. Counts are AI updates (I-ticks; an angry monster gets 1.5x as many).
 */
export const ATTACK = {
  /** Once allowed, 1 in 4 an update (`jsr D_E9EA; and #$03; bne`). */
  chance: 4,
  /** A monster lower than its player tries only 1 in 32 of those (`and #$1f`); higher, never. */
  lowerChance: 32,
  /** Facing right it fires only at X < $F4, facing left at X >= $34 (`cmp #$f4`, `cmp #$34`). */
  maxX: 0xf4,
  minX: 0x34,
  /** Wind-up: D_8818 = 7, the shot leaves on the update that counts it to 0 (L_EE31, D_ED3B). */
  windUp: 7,
  /** Cooldown after an attack (`lda #$96; sta D_8890,x`); it counts once the wind-up is over. */
  cooldown: 150,
  /** The shot's column: 2 ahead of the monster's own (L_ED68, L_ED71). */
  ahead: 2,
  /** Drunk waits for its bottle from D_8818 = $2C (L_EDAA); its return sets a cooldown of 100 (L12DE). */
  drunkWait: 0x2c,
  bottleCooldown: 100,
} as const;

/** The Invader's laser (02 §5 type 7; D_1E9F). */
export const LASER = {
  /** Chance per update: a random byte below 12 (`cmp #$0c; bcs`). */
  chance: 12,
  /** Only while its Y is above $A8 (`cmp #$a8; bcs`). */
  maxY: 0xa8,
  /** Cooldown and "laser out" count (`lda #$64; sta D_8890,x; sta D_8818,x`). */
  cooldown: 100,
  /** Starts at column (X - 12) / 8 and row (Y - 5) / 8: under the Invader's feet, a column right. */
  fromX: 12,
  fromY: 5,
  /** Falls a row a pass until row $1D, below the screen (L12F9 `cmp #$1d`). */
  endRow: 0x1d,
} as const;

/** Software slots a monster's shot may take: 16 (BLNON 0..15, slots 2..17: `ldy #$0f`, L_EE25, $1EC0). */
export const SHOT_SLOTS = 16;

/** A shot's cell to VIC frame: x = 8col + $14, y = 8row + $15, as a bubble's. */
export const SHOT_BASE_X = 0x14;
export const SHOT_BASE_Y = 0x15;
/** Kill box (L152B): |Px - (8col + $18)| < 16 and |Py - (8row + $15)| < 16 kills; then it returns. */
export const SHOT_HIT_X = 0x18;
export const SHOT_REACH = 16;
/**
 * After it stops a shot's slot runs the burst passes of D_E90E: type $3A shows the two burst
 * pictures two passes each, one hidden pass, freed on the sixth; $38 is hidden at once, freed on the
 * second (routine_3D77, routine_3CD3..3CDF, D_3DA9, D_7BDB).
 */
export const BURST_PASSES = 6;
export const VANISH_PASSES = 2;

/** Level 100's boss (02 §9.4): D_1D84 (movement, touch), special_item_handler $1134 (bolts, stun). */
export const BOSS = {
  /** Where it appears, as the READY banner goes ($16C1: X $74, Y $65, moving up and left). */
  x: 0x74,
  y: 0x65,
  /** I-ticks of play before that: the banner's 2 seconds (D_A9B1 = 2, one a second). */
  wake: 50,
  /** It turns when X < $28 or >= $C0, Y < $3A or >= $B8, moving 2 px a move (D_1D96). */
  minX: 0x28,
  maxX: 0xc0,
  minY: 0x3a,
  maxY: 0xb8,
  step: 2,
  /** A side bounce flips the picture set ($BE `eor #$18`). */
  flip: 0x18,
  /** Touch: -8 <= Px - Bx < $40 and -8 <= Py - By < $38 (L_1DEC); the bolt test is the same ($1134). */
  near: 8,
  reachX: 0x40,
  reachY: 0x38,
  /**
   * Bolts it takes: D_5BBF = $32 and it is stunned when a hit takes it below 0 - 51 bolts ($2C75,
   * skip_item_check); after a stun $28, so 41 ($1194). `Boss.hp` counts the bolts left.
   */
  hits: 0x32 + 1,
  recoverHits: 0x28 + 1,
  /** M-ticks stunned (D_5BBF = $82), blinking below $11: its sprites toggle on every even count ($11A3). */
  stun: 0x82,
  blinkFrom: 0x10,
  /** The win: 1,000,000 counted up $10 BCD (10,000) a frame over 100 frames (D_A5B7); here per I-tick. */
  payoutTicks: 50,
  payoutUnits: 2000,
  /** Colours ($C0): green; light red once angry (D_1D84 `lda #$0a; sta $c0`). */
  colour: 5,
  angryColour: 10,
} as const;

/** SUPORR rewrites the enemy tables of the first 99 levels only (`ldx #$63` before L_45F2). */
export const SUPORR_LAST_LEVEL = 99;
